import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarIcon } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { formatDisplayDate } from "@/lib/date-formatters";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import DashboardKpiCards from "@/components/admin/DashboardKpiCards";
import DashboardDailyChart from "@/components/admin/DashboardDailyChart";
import DashboardStatusBreakdown from "@/components/admin/DashboardStatusBreakdown";
import DashboardTopProducts from "@/components/admin/DashboardTopProducts";
import DashboardTopCategories from "@/components/admin/DashboardTopCategories";
import DashboardShippingBreakdown from "@/components/admin/DashboardShippingBreakdown";
import { cn } from "@/lib/utils";

interface DashboardData {
  orders: number;
  revenue: number;
  avgOrderValue: number;
  totalProducts: number;
  totalCustomers: number;
  statusBreakdown: { status: string; count: number }[];
  topProducts: { id: number; name: string; sku: string | null; count: number; revenue: number }[];
  topCategories: { name: string; count: number; revenue: number }[];
  dailyOrders: { date: string; count: number; revenue: number }[];
  shippingBreakdown: { name: string; is_pickup: number; count: number }[];
}

export default function Dashboard() {
  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(today.getDate() - 30);

  const [from, setFrom] = useState<Date>(thirtyDaysAgo);
  const [to, setTo] = useState<Date>(today);

  function applyPreset(days: number) {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    setFrom(start);
    setTo(end);
  }

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "dashboard", from.toISOString().slice(0, 10), to.toISOString().slice(0, 10)],
    queryFn: () =>
      apiFetch<DashboardData>(
        `/api/admin/dashboard?from=${from.toISOString().slice(0, 10)}&to=${to.toISOString().slice(0, 10)}`
      ),
  });

  if (isLoading || !data) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="size-8 animate-spin rounded-full border-2 border-foreground border-t-transparent" />
      </div>
    );
  }

  const maxDailyCount = Math.max(...(data.dailyOrders.map((d) => d.count) ?? [1]), 1);
  const maxStatusCount = Math.max(...(data.statusBreakdown.map((s) => s.count) ?? [1]), 1);

  const deliveryItems = data.shippingBreakdown.filter((s) => !s.is_pickup);
  const pickupItems = data.shippingBreakdown.filter((s) => s.is_pickup);
  const deliveryCount = deliveryItems.reduce((sum, s) => sum + s.count, 0);
  const pickupCount = pickupItems.reduce((sum, s) => sum + s.count, 0);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl sm:text-2xl font-bold">Pregled</h1>

        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => applyPreset(7)} className="text-xs h-8">
            7 dana
          </Button>
          <Button variant="outline" size="sm" onClick={() => applyPreset(30)} className="text-xs h-8">
            30 dana
          </Button>
          <Button variant="outline" size="sm" onClick={() => applyPreset(90)} className="text-xs h-8">
            90 dana
          </Button>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className={cn("text-xs h-8 gap-1.5")}>
                <CalendarIcon className="size-3.5" />
                {formatDisplayDate(from)} — {formatDisplayDate(to)}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-4" align="end">
              <div className="flex gap-4 flex-wrap">
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2">Od</p>
                  <Calendar mode="single" selected={from} onSelect={(d) => d && setFrom(d)} />
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2">Do</p>
                  <Calendar mode="single" selected={to} onSelect={(d) => d && setTo(d)} />
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <DashboardKpiCards
        orders={data.orders}
        revenue={data.revenue}
        avgOrderValue={data.avgOrderValue}
        totalCustomers={data.totalCustomers}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <DashboardDailyChart dailyOrders={data.dailyOrders} maxCount={maxDailyCount} />
        <DashboardStatusBreakdown items={data.statusBreakdown} maxCount={maxStatusCount} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <DashboardTopProducts products={data.topProducts} />
        <DashboardTopCategories categories={data.topCategories} />
      </div>

      <DashboardShippingBreakdown
        items={data.shippingBreakdown}
        deliveryCount={deliveryCount}
        pickupCount={pickupCount}
      />
    </div>
  );
}
